import { POSBaseInfoContext } from '@/components/LayoutPOS/Synchronizer';
import { message } from '@/components/Message';
import Modal from '@/components/Modal';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { DateTimeFormat } from '@/models/common';
import { PreOrder, setOperatorForPreOrder } from '@/models/order';
import { deleteService, getIsPaidOrder, useSyncFromServer } from '@/models/preOrder';
import { IS_POS } from '@/utils/env';
import { playSound } from '@/utils/sound';
import { ExclamationCircleOutlined, ReloadOutlined, TableOutlined } from '@ant-design/icons';
import { Empty, Typography } from 'antd';
import { APIError } from 'api/types';
import { produce } from 'immer';
import React, { ComponentProps, Ref, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { ConfirmModal } from './ConfirmModal';
import { StationItem } from './item';
import { useStatinoStyles } from './styles';
import { useOrderHistory } from './useOrderHistory';

export type StationRef = {
  deletePreOrder: (preOrder: PreOrder) => Promise<void>;
  push: (preOrder: PreOrder) => Promise<PreOrder>;
} & Omit<ReturnType<typeof useOrderHistory>, 'push'>;

type Prop = {
  onPop: (item: PreOrder) => void;
  onRequestDelete: (item: PreOrder) => void;
  onStationChange: () => void;
  showOverrideWarning: boolean;
  readonly?: boolean;
  title?: React.ReactNode;
};
export const Station = React.forwardRef(
  (
    {
      onPop,
      onRequestDelete,
      showOverrideWarning,
      onStationChange,
      readonly = false,
      title = (
        <>
          <TableOutlined /> 服务台
        </>
      ),
    }: Prop,
    ref: Ref<StationRef>
  ) => {
    const { styles } = useStatinoStyles({ readonly });
    const [, , { showSelectOperatorModal }] = useContext(BaseInfoContext);
    const { getOperatorList } = useContext(POSBaseInfoContext);
    const { history, push, pop, popByOrderSn, empty, size, updateItem } = useOrderHistory(readonly);
    const {
      payload: remoteHistory = {},
      send: fetchRemoteHistory,
      loading: remoteHistoryLoading,
    } = useSyncFromServer();
    // 预约、服务时长
    const [confirmInfo, setConfirmInfo] = useState<ComponentProps<typeof ConfirmModal>['confirmInfo']>();

    // readonly 模式从远程获取数据
    useEffect(() => {
      if (readonly) {
        fetchRemoteHistory();
        // const timer = setInterval(runner, 3000);
        // return () => {
        //   console.log('clear');
        //   clearInterval(timer);
        // };
      }
    }, []);

    useEffect(() => {
      onStationChange();
    }, [size]);

    const pushTo = async (preOrder: PreOrder) =>
      new Promise<PreOrder>(resolve => {
        const done = (order: PreOrder) => {
          push(order);
          resolve(order);
        };
        done(preOrder);
      });

    const deletePreOrder = async (preOrder: PreOrder) =>
      new Promise<void>((resolve, reject) => {
        Modal.confirm({
          title: '撤单二次确认',
          content: getIsPaidOrder(preOrder) ? '订单当前正在服务中，确认要撤单吗？' : '是否确认撤单',
          onOk: async () => {
            // 从记录中移除
            pop(preOrder);

            // 已经开始的服务单，修改预约与订单
            if (preOrder.startTime && (preOrder.appointment || preOrder.orderSn)) {
              try {
                await deleteService({ id: preOrder.appointment.id, orderSn: preOrder.orderSn });
                resolve();
              } catch (e) {
                Modal.error({
                  title: '撤单失败',
                  content: `原因: ${(e as APIError).msg}, 请联系店长处理`,
                });
                reject();
              }
            } else {
              // 未开始的订单，直接 resolve
              resolve();
            }
          },
        });
      });

    useImperativeHandle(ref, () => {
      return {
        deletePreOrder,
        history,
        push: pushTo,
        pop,
        popByOrderSn,
        empty,
        updateItem,
        // getSize: () => size,
        size,
      };
    }, [history, push, pop, empty, size]);

    const records = readonly ? remoteHistory : history;

    return (
      <div className={`${styles.container} ${IS_POS ? styles.pos : ''}`}>
        <h3 className={styles.head}>
          <div className={styles.headCenter}>{title}</div>
          <div className={styles.headActions}>
            {readonly && (
              <ReloadOutlined
                spin={remoteHistoryLoading}
                onClick={async () => {
                  if (remoteHistoryLoading) return;
                  await fetchRemoteHistory();
                  message.success('更新成功');
                }}
              />
            )}
          </div>
        </h3>
        <div className={styles.center}>
          {confirmInfo ? (
            <ConfirmModal
              confirmInfo={confirmInfo}
              onClose={() => {
                setConfirmInfo(null);
              }}
              onConfirmed={(appointment, preOrder, userFullInfo, baseTime) => {
                setConfirmInfo(null);
                updateItem(
                  produce(preOrder, draft => {
                    draft.appointment = appointment;
                    draft.userFullInfo = userFullInfo;
                    draft.startTime = baseTime.format(DateTimeFormat);
                  })
                );
              }}
            />
          ) : null}
          {!Object.entries(records).length ? (
            <Empty
              className="mt-m"
              image={Empty.PRESENTED_IMAGE_DEFAULT}
              description={
                readonly ? (
                  <Typography.Paragraph>当前无服务</Typography.Paragraph>
                ) : (
                  <Typography.Paragraph>
                    点击<Typography.Text strong>「创建服务」</Typography.Text>
                    <br />
                    以添加服务单到此处
                  </Typography.Paragraph>
                )
              }
            />
          ) : (
            <div className={styles.itemWrapper}>
              {Object.entries(records)
                .reverse()
                .map(([_, preOrder], index) => {
                  return (
                    <StationItem
                      key={`${preOrder.key}`}
                      preOrder={preOrder}
                      no={Object.keys(records).length - index}
                      readonly={readonly}
                      onRequestDelete={onRequestDelete}
                      onRequestPop={preOrder => {
                        const isPaidOrder = getIsPaidOrder(preOrder);

                        const confirmPop = () => {
                          if (isPaidOrder) {
                            // 有 ordersn 的订单，只查看不移出
                            onPop(preOrder);
                          } else {
                            pop(preOrder);
                            onPop(preOrder);
                          }
                        };

                        if (showOverrideWarning && !isPaidOrder) {
                          Modal.confirm({
                            autoFocusButton: 'ok',
                            cancelButtonProps: { size: 'large' },
                            okButtonProps: { size: 'large' },
                            title: '取单',
                            icon: <ExclamationCircleOutlined />,
                            content: <>待结清单中存在已添加的服务或商品，取出的数据将覆盖当前数据，是否继续?</>,
                            onOk() {
                              confirmPop();
                            },
                          });
                        } else {
                          confirmPop();
                        }
                      }}
                      onRequestChangeOperator={preOrder => {
                        // 变更操作员
                        showSelectOperatorModal({
                          visible: true,
                          operatorList: getOperatorList(),
                          value: preOrder.operator,
                          onChange: async staff => {
                            playSound('click_light');
                            const order = await setOperatorForPreOrder(preOrder, staff);
                            updateItem(order);
                            message.success('美容师已变更');
                          },
                          onCancel: () => {
                            playSound('click');
                          },
                        });
                      }}
                      onRequestSetConfirmInfo={setConfirmInfo}
                    />
                  );
                })}
            </div>
          )}
        </div>
      </div>
    );
  }
);
